import { useEffect, useRef, useState } from 'react';
import { HOW_TO_PLAY, type GameId } from '@ofc/shared';
import { gameTitle } from '../screens/Landing';

const SEEN_PREFIX = 'ofc:howto:';

/** Whether this phone has already been shown how a game works. */
export function hasSeenHowTo(game: GameId): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + game) === '1';
  } catch {
    // Storage blocked: better to show the slides again than never.
    return false;
  }
}

function markSeen(game: GameId): void {
  try {
    localStorage.setItem(SEEN_PREFIX + game, '1');
  } catch {
    // Nothing to do; they will just see it again next time.
  }
}

/**
 * Three slides on how a game works: what you do, how you score, one tip.
 *
 * Shown by itself the first time a phone opens a game, and from the ? in the
 * room's top bar after that. Swipe or tap through; any way out counts as seen.
 */
export function HowToPlay({ game, onClose }: { game: GameId; onClose(): void }) {
  const slides = HOW_TO_PLAY[game];
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const slide = slides[index]!;
  const last = index === slides.length - 1;

  const close = () => {
    markSeen(game);
    onClose();
  };

  useEffect(() => {
    nextRef.current?.focus();
  }, [index]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') setIndex((i) => Math.min(slides.length - 1, i + 1));
      if (event.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // The slide count never changes while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sheet-backdrop" role="presentation" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="howto-title"
        className="panel col sheet"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => (touchX.current = event.touches[0]?.clientX ?? null)}
        onTouchEnd={(event) => {
          const start = touchX.current;
          const end = event.changedTouches[0]?.clientX;
          touchX.current = null;
          if (start === null || end === undefined || Math.abs(end - start) < 40) return;
          setIndex((i) => (end < start ? Math.min(slides.length - 1, i + 1) : Math.max(0, i - 1)));
        }}
        style={{ gap: 14, padding: '20px 18px' }}
      >
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="sign small muted" style={{ letterSpacing: '0.18em' }}>
            How to play · {gameTitle(game)}
          </span>
          <button className="btn-ghost small" onClick={close} aria-label="Close" style={{ minHeight: 36, border: 0 }}>
            ✕
          </button>
        </div>

        <div key={index} className="col center entrance-fade" style={{ gap: 10, minHeight: 190, justifyContent: 'center' }}>
          <div aria-hidden style={{ fontSize: 54, lineHeight: 1 }}>
            {slide.emoji}
          </div>
          <h2 id="howto-title" className="sign neon neon-pink" style={{ margin: 0, fontSize: 22, textAlign: 'center' }}>
            {slide.title}
          </h2>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.45, textAlign: 'center' }}>{slide.body}</p>
        </div>

        <div className="row" style={{ justifyContent: 'center', gap: 8 }} aria-hidden>
          {slides.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === index ? 22 : 8,
                height: 8,
                borderRadius: 99,
                background: i === index ? 'var(--pink)' : 'var(--panel-line)',
                transition: 'width 0.2s ease',
              }}
            />
          ))}
        </div>

        <div className="row" style={{ gap: 8 }}>
          {index > 0 && (
            <button className="btn-ghost grow" onClick={() => setIndex(index - 1)}>
              Back
            </button>
          )}
          <button
            ref={nextRef}
            className="btn-primary grow"
            onClick={() => (last ? close() : setIndex(index + 1))}
          >
            {last ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
